import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { DataServiceError, DefaultDataServiceConfig, EntityActionFactory, EntityOp, HttpUrlGenerator } from '@ngrx/data';
import { Store } from '@ngrx/store';
import moment from 'moment';
import { LoggingService } from '../../shared/services/logging/logging.service';
import { EfaactDataService } from '../efaact.data-service';
import { Merge } from './merge.model';
import { catchError, map } from 'rxjs/operators';
import { Observable, throwError } from 'rxjs';
import { IGenericResponse } from '../../common';
import { Update } from '@ngrx/entity';
import * as fromShared from '../../shared';
import * as fromLookups from '../../lookups';

export const entityName = 'Merge';

@Injectable()
export class MergeDataService extends EfaactDataService<Merge> {
    constructor(
        http: HttpClient,
        httpUrlGenerator: HttpUrlGenerator,
        log: LoggingService,
        config: DefaultDataServiceConfig,
        store: Store
    ) {
        super(http, httpUrlGenerator, entityName, log, config, store);
    }

    protected save(update: Update<Merge>): Observable<Merge> {
        this.log.verbose({
            type: `${this.entityName}DataService(EfaactDataService):save`,
            update
        });
        const item = {
            ...update.changes
        } as any;
        delete item.original;
        return this.http
            .put<IGenericResponse<Merge>>(this.entitiesUrl, {
                [`${this.entityName}List`]: [item]
            })
            .pipe(
                map(resp => {
                    this.log.verbose({
                        type: `${this.entityName}DataService(EfaactDataService):save:resp`,
                        resp
                    });
                    if (!fromShared.Errors.IsSuccessStatus(resp.StatusCode)) {
                        this.store.dispatch(
                            new fromShared.Errors.SetErrors(
                                fromShared.Errors.MapErrorsToStatus({
                                    ...resp,
                                    Items: resp.Items.filter(x => !!x).map(
                                        x => ({
                                            ...x,
                                            Status: {
                                                ...x.Status,
                                                MsgEntityID:
                                                    update.changes.original &&
                                                    update.changes.original.id
                                            }
                                        })
                                    )
                                })
                            )
                        );
                        throwError(
                            new DataServiceError(
                                fromShared.Errors.getResponseErrors(
                                    resp
                                ).reduce(
                                    (a, c, i) => (i === 0 ? c : `${a}\n${c}`),
                                    ''
                                ),
                                { method: 'PUT', url: this.entitiesUrl }
                            )
                        );
                        return {
                            ...update.changes,
                            ...update.changes.original,
                            ...resp.Items[0],
                            Status:
                                resp.Items[0] && resp.Items[0].Status
                                    ? resp.Items[0].Status
                                    : {
                                          StatusCode: resp.StatusCode,
                                          Message: resp.StatusType
                                      }
                        } as Merge;
                    }
                    if (
                        resp.StatusCode === fromShared.ResponseStatus.Warning &&
                        resp.Items.some(x => !x.Status.MsgEntityID)
                    ) {
                        this.log.verbose({
                            type: `${this.entityName}DataService(EfaactDataService):save:ResponseStatus.Warning`,
                            resp
                        });
                        this.store.dispatch(
                            new fromShared.Errors.SetErrors(
                                fromShared.Errors.MapErrorsToStatus({
                                    ...resp,
                                    Items: resp.Items.filter(
                                        x =>
                                            !!x &&
                                            x.Status &&
                                            !x.Status.MsgEntityID
                                    )
                                })
                            )
                        );
                    }
                    this.store.dispatch(new fromLookups.LoadTasks());
                    this.store.dispatch(new fromLookups.LoadServiceItems(false));
                    const result = {
                        ...this.mapItems({
                            id:
                                update && update.changes && update.changes.id
                                    ? update.changes.id
                                    : update.changes.original
                                    ? update.changes.original.id
                                    : null,
                            ...resp.Items[0]
                        }),
                        Status: {
                            StatusCode: resp.StatusCode,
                            Message:
                                resp.Items &&
                                resp.Items[0] &&
                                resp.Items[0].Status &&
                                resp.Items[0].Status.Message
                                    ? resp.Items[0].Status.Message
                                    : resp.StatusType
                        }
                    } as Merge;
                    this.log.verbose({
                        type: `${this.entityName}DataService(EfaactDataService):result`,
                        result
                    });
                    return result;
                }),
                catchError((e, c) =>
                    fromShared.Errors.handleDataServiceError(this.store, e, c)
                )
            );
    }

    mapItems(item: Merge): Merge {
        if (!item) {
            return;
        }
        return {
            ...item,
            id: `${item.Fromlistid}-${item.Finallistid}`
        };
    }
}
